import { cn } from "@/utils/cnHelper";
import { faChevronDown } from "@icons/regular";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { Icon } from "@ui";
import * as React from "react";

/*
 * @example
 * ```
 * import {
 *   Accordion,
 *   AccordionContent,
 *   AccordionItem,
 *   AccordionTrigger,
 * } from "@ui"
 *
 * <Accordion type="single" collapsible>
 *   <AccordionItem value="item-1">
 *     <AccordionTrigger>Trigger Text</AccordionTrigger>
 *     <AccordionContent>
 *       Accordion Content goes here.
 *     </AccordionContent>
 *   </AccordionItem>
 * </Accordion>
 * ```
 */

function Accordion({ ...props }: React.ComponentProps<typeof AccordionPrimitive.Root>) {
  return <AccordionPrimitive.Root data-slot="accordion" {...props} />;
}

function AccordionItem({ className, ...props }: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  return <AccordionPrimitive.Item data-slot="accordion-item" className={cn("tw-border-b last:tw-border-b-0", className)} {...props} />;
}

function AccordionTrigger({ className, children, ...props }: React.ComponentProps<typeof AccordionPrimitive.Trigger>) {
  return (
    <AccordionPrimitive.Header className="tw-flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          "tw-flex tw-flex-1 tw-items-start tw-justify-between tw-gap-4 tw-rounded-md tw-py-4 tw-text-left tw-text-sm tw-font-medium tw-outline-none tw-transition-all hover:tw-underline focus-visible:tw-relative focus-visible:tw-z-10 focus-visible:tw-shadow-[0_0_0_2px] focus-visible:tw-shadow-brand_scale-500 disabled:tw-pointer-events-none disabled:tw-opacity-50 [&[data-state=open]>svg]:tw-rotate-180",
          className
        )}
        {...props}
      >
        {children}
        <Icon
          icon={faChevronDown}
          className="tw-text-muted-foreground tw-pointer-events-none tw-size-4 tw-shrink-0 tw-translate-y-0.5 tw-transition-transform tw-duration-200"
        />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

function AccordionContent({ className, children, ...props }: React.ComponentProps<typeof AccordionPrimitive.Content>) {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className="tw-overflow-hidden tw-text-sm data-[state=closed]:tw-animate-accordionUp data-[state=open]:tw-animate-accordionDown"
      {...props}
    >
      <div className={cn("tw-pb-4 tw-pt-0", className)}>{children}</div>
    </AccordionPrimitive.Content>
  );
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
